/**
 * VocabLanding.jsx — rebuilt from Bloom_Home_Redesign.html
 *
 * Image assets — place in /public/assets/:
 *   word-metaphor.webp  word-peep.webp  word-famine.webp  word-futile.webp
 *   quiz-vile.webp  quiz-futile.webp  word-attire.webp
 *
 * The Mascot component covers the bloom-mark logo.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogo, Sparkle, ListChecks, Trophy, MagnifyingGlass, ArrowsClockwise, Check, Plus, ArrowRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { useAuth } from "./AuthContext";

// ── Slide data ────────────────────────────────────────────────────────────────
const SLIDES = [
  { word: "Metaphor", src: "/assets/word-metaphor.webp", alt: "Metaphor illustrated word card", caption: "Illustrated cards make new words stick" },
  { word: "Peep",     src: "/assets/word-peep.webp",     alt: "Peep word card with synonyms and antonyms", caption: "Synonyms and antonyms, a tap away" },
  { word: "Famine",   src: "/assets/word-famine.webp",   alt: "Famine word card — Grammar School vocab", caption: "Grammar School and 11+ vocabulary included" },
  { word: "Futile",   src: "/assets/word-futile.webp",   alt: "Futile and Pointless comparison card", caption: "Side-by-side cards for words that are easy to mix up" },
];

const ANNUAL_FEATURES  = ["Up to 2 children per family", "All words, learning plans & dashboard", "Request up to 20 new words a month"];
const MONTHLY_FEATURES = ["Up to 2 children per family", "All words, learning plans & dashboard", "Request up to 20 new words a month"];

const TRIAL_STEPS = [
  { when: "Today",    bg: "#FFE4D4", fg: "#B13C0A", title: "Sign in with Google",   body: "Add your children in a minute. No card needed." },
  { when: "Day 5",   bg: "#FFEFC9", fg: "#8A5300", title: "A friendly reminder",    body: "A banner in your dashboard and an email, with 2 days to go." },
  { when: "Day 7",   bg: "#D9F3E6", fg: "#16704B", title: "Choose what suits you",  body: "Monthly, Annual or a 7-day pass. Nothing is charged unless you pick one." },
  { when: "Any time",bg: "#DCEEF6", fg: "#1D6485", title: "Billing in one place",   body: "Change plan, view invoices or cancel from the Billing tab." },
];

// ── Learning plan data ────────────────────────────────────────────────────────
const LEVEL_COLORS = { easy: ["#D9F3E6", "#16704B"], medium: ["#FFEFC9", "#8A5300"], hard: ["#FFE4D4", "#B13C0A"] };

const PLAN_STEPS = [
  { n: 1, bg: "#FFE4D4", fg: "#B13C0A", title: "Name it",         body: "A short name like “Week 1” and an icon your child will recognise." },
  { n: 2, bg: "#FFEFC9", fg: "#8A5300", title: "Pick the words",  body: "Auto lets Bloom decide. Manual gives you full control." },
  { n: 3, bg: "#D9F3E6", fg: "#16704B", title: "Set quiz length", body: "Choose the quiz length and the plan is ready to practise." },
];

const PLAN_MODES = {
  auto: {
    title: "Bloom decides",
    body: "Bloom looks at what your child knows well, is struggling with and has never seen, then decides how many words to include and which ones.",
    points: ["Words at the right level for their year", "Struggling words come back for more practice", "Swap any word with one tap"],
    tagBg: "#FFE4D4", tagFg: "#B13C0A",
    words: [
      ["Benevolent", "hard",   "Kind, caring, and willing to help other people"],
      ["Amble",      "medium", "To walk slowly and calmly, often in a relaxed way."],
      ["Aisle",      "easy",   "A passage between rows of seats or shelves."],
      ["Bolster",    "medium", "To support, strengthen, or help someone or something."],
      ["Aptitude",   "medium", "Having a natural ability to do something well."],
    ],
  },
  manual: {
    title: "You decide",
    body: "Full control. Search every word in Bloom and build the list yourself.",
    points: ["Search by word or meaning", "Sort A–Z or by recently added", "See each word's difficulty before you add it"],
    tagBg: "#D9F3E6", tagFg: "#16704B",
    words: [
      ["Abode",      "medium", "A place where someone lives, like a house or home."],
      ["Aisle",      "easy",   "A passage between rows of seats or shelves."],
      ["Amble",      "medium", "To walk slowly and calmly, often in a relaxed way."],
      ["Benevolent", "hard",   "Kind, caring, and willing to help other people"],
      ["Beverage",   "easy",   "Any drink, especially one that is not water."],
    ],
  },
};

// ── Shared style helpers ───────────────────────────────────────────────────────
const S = {
  root: { containerType: "inline-size", position: "relative", minHeight: "100%", background: "#FFF7F0", color: "#262A44", fontFamily: "'Quicksand', system-ui, sans-serif", fontWeight: 500, WebkitFontSmoothing: "antialiased" },
  h1:   { margin: 0, fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(40px,8.4cqw,72px)", lineHeight: 1.02, letterSpacing: "-.03em", textWrap: "balance" },
  h2:   { margin: 0, fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(28px,5cqw,44px)", lineHeight: 1.08, letterSpacing: "-.02em", textWrap: "balance" },
  h2sm: { margin: 0, fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(26px,4.4cqw,38px)", lineHeight: 1.1,  letterSpacing: "-.02em" },
  body: { margin: 0, fontSize: "clamp(17px,2.2cqw,20px)", lineHeight: 1.5, color: "#545872", textWrap: "pretty" },
  label:{ font: "700 13px 'Quicksand',sans-serif", letterSpacing: ".14em" },
  check: { flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: "#D9F3E6", color: "#16704B", display: "grid", placeItems: "center", font: "800 13px 'Nunito',sans-serif" },
  ctaBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, width: "100%", height: 60, border: 0, borderRadius: 999, background: "#F76B32", color: "#FFFFFF", font: "800 19px 'Nunito',sans-serif", letterSpacing: ".005em", cursor: "pointer", boxShadow: "0 10px 24px rgba(247,107,50,.32)" },
  section: (extra) => ({ maxWidth: 1240, margin: "0 auto", padding: "clamp(56px,8cqw,96px) clamp(20px,5cqw,48px)", ...extra }),
};

// ── Sub-components ────────────────────────────────────────────────────────────
function CheckItem({ text }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <span style={S.check}>✓</span>
      <span style={{ font: "500 16px/1.5 'Quicksand',sans-serif", color: "#3E4260", textWrap: "pretty" }}>{text}</span>
    </div>
  );
}

function GButton({ onClick, style: extraStyle, children }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ ...S.ctaBtn, background: hov ? "#EC5E24" : "#F76B32", ...extraStyle }}
    >
      <span style={{ width: 28, height: 28, borderRadius: "50%", background: "#FFFFFF", color: "#F76B32", display: "grid", placeItems: "center" }}>
        <GoogleLogo size={16} weight="bold" />
      </span>
      {children}
    </button>
  );
}

function LearningPlanSection() {
  const [mode, setMode] = useState("auto");
  const [picked, setPicked] = useState({ Abode: true, Aisle: true, Amble: true });
  const auto = mode === "auto";
  const m = PLAN_MODES[mode];
  const pickedCount = Object.values(picked).filter(Boolean).length;
  const toggleWord = (w) => setPicked(p => ({ ...p, [w]: !p[w] }));

  const tab = (key, label, Icon) => {
    const on = mode === key;
    return (
      <button key={key} role="tab" aria-selected={on} onClick={() => setMode(key)}
        style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, height: 50, padding: "0 12px", border: 0, borderRadius: 999, background: on ? "#FFFFFF" : "transparent", color: on ? "#262A44" : "#6E7289", boxShadow: on ? "0 2px 8px rgba(38,42,68,.10)" : "none", font: "800 16px 'Nunito',sans-serif", cursor: "pointer", transition: "background .2s,color .2s" }}>
        <Icon size={18} weight="fill" />{label}
      </button>
    );
  };

  return (
    <section>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(56px,8cqw,96px) clamp(20px,5cqw,48px)", display: "flex", flexDirection: "column", gap: "clamp(28px,4cqw,40px)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 680 }}>
          <span style={{ ...S.label, color: "#1D6485" }}>FOR PARENTS</span>
          <h2 style={S.h2}>Create a learning plan for each child</h2>
          <p style={{ margin: 0, font: "500 17px/1.6 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>Three quick steps. Let Bloom pick the words, or choose every one yourself.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,240px),1fr))", gap: 14 }}>
          {PLAN_STEPS.map(({ n, bg, fg, title, body }) => (
            <div key={n} style={{ padding: "20px 22px 22px", borderRadius: 22, background: "#FFFFFF", border: "1px solid #F0E4D8", display: "flex", flexDirection: "column", gap: 8 }}>
              <span style={{ width: 32, height: 32, borderRadius: "50%", background: bg, color: fg, display: "grid", placeItems: "center", font: "900 15px 'Nunito',sans-serif" }}>{n}</span>
              <span style={{ font: "900 19px 'Nunito',sans-serif" }}>{title}</span>
              <span style={{ font: "500 15px/1.5 'Quicksand',sans-serif", color: "#4A4E68", textWrap: "pretty" }}>{body}</span>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,400px),1fr))", gap: "clamp(28px,5cqw,56px)", alignItems: "center" }}>
          {/* Toggle + explanation */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 500, minWidth: 0 }}>
            <div role="tablist" aria-label="How words are picked" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, padding: 5, borderRadius: 999, background: "#F4EEE8", width: "100%", maxWidth: 420, boxSizing: "border-box" }}>
              {tab("auto", "Auto", Sparkle)}
              {tab("manual", "Manual", ListChecks)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <span style={{ font: "900 24px/1.2 'Nunito',sans-serif" }}>{m.title}</span>
              <p style={{ margin: 0, font: "500 16.5px/1.6 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>{m.body}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
                {m.points.map(p => <CheckItem key={p} text={p} />)}
              </div>
            </div>
          </div>

          {/* Plan preview */}
          <div style={{ width: "100%", maxWidth: 520, justifySelf: "center", minWidth: 0, borderRadius: 28, background: "#FFFDFA", border: "1px solid #F0E4D8", boxShadow: "0 18px 44px rgba(38,42,68,.10)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 20px", borderBottom: "1px solid #F0E4D8" }}>
              <span style={{ flexShrink: 0, width: 42, height: 42, borderRadius: "50%", background: "#F76B32", color: "#FFFFFF", display: "grid", placeItems: "center" }}><Trophy size={20} weight="fill" /></span>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ font: "900 18px 'Nunito',sans-serif" }}>Week 1</span>
                <span style={{ font: "600 13.5px 'Quicksand',sans-serif", color: "#6E7289" }}>Soniya · Year 6</span>
              </div>
              <span style={{ marginLeft: "auto", padding: "5px 11px", borderRadius: 999, background: m.tagBg, color: m.tagFg, font: "800 12.5px 'Nunito',sans-serif", whiteSpace: "nowrap" }}>{auto ? "Auto" : "Manual"}</span>
            </div>

            {auto ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 20px 6px", padding: "12px 14px", borderRadius: 16, background: "#FFF1E8", color: "#B13C0A", font: "700 14.5px/1.4 'Quicksand',sans-serif" }}>
                <Sparkle size={18} weight="fill" style={{ flexShrink: 0 }} />
                <span>Bloom picked 12 words for Soniya</span>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 20px 6px", height: 48, padding: "0 16px", borderRadius: 999, background: "#F7F4F1", border: "1.5px solid #ECE6DF", color: "#8B8FA3", font: "500 15px 'Quicksand',sans-serif" }}>
                <MagnifyingGlass size={17} weight="bold" color="#545872" />
                <span>Search words or meanings…</span>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column" }}>
              {m.words.map(([word, level, def]) => {
                const on = auto || !!picked[word];
                const [chipBg, chipFg] = LEVEL_COLORS[level];
                const Icon = auto ? ArrowsClockwise : on ? Check : Plus;
                return (
                  <div key={word} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: "1px solid #F4ECE4", background: !auto && on ? "#EEF8F2" : "transparent" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ font: "800 16px 'Nunito',sans-serif" }}>{word}</span>
                        <span style={{ padding: "2px 9px", borderRadius: 999, background: chipBg, color: chipFg, font: "700 12px 'Quicksand',sans-serif" }}>{level}</span>
                      </div>
                      <span style={{ font: "500 13.5px/1.4 'Quicksand',sans-serif", color: "#545872", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{def}</span>
                    </div>
                    <button onClick={auto ? undefined : () => toggleWord(word)} aria-label={auto ? `Swap ${word}` : on ? `Remove ${word}` : `Add ${word}`}
                      style={{ flexShrink: 0, width: 36, height: 36, padding: 0, borderRadius: "50%", border: auto ? "1.5px solid #ECE6DF" : on ? 0 : "1.5px solid #E3DCD4", background: !auto && on ? "#1FC98A" : "#FFFFFF", color: auto ? "#6E7289" : on ? "#FFFFFF" : "#545872", display: "grid", placeItems: "center", cursor: "pointer" }}>
                      <Icon size={16} weight="bold" />
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "16px 20px 20px" }}>
              <span style={{ font: "800 15px 'Nunito',sans-serif", color: "#3E4260" }}>{auto ? "+ 7 more words" : `${pickedCount} word${pickedCount === 1 ? "" : "s"} selected`}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 8, height: 46, padding: "0 20px", borderRadius: 999, background: "#F76B32", color: "#FFFFFF", font: "800 15.5px 'Nunito',sans-serif", boxShadow: "0 8px 20px rgba(247,107,50,.28)" }}>
                Set quiz length <ArrowRight size={16} weight="bold" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


// ── Word request data ─────────────────────────────────────────────────────────
const WR_WORD = "Attire";
const WR_WORK = [
  ["Checking the word…", "Making sure it's right for young learners"],
  ["Writing the meaning…", "In clear, child-friendly language"],
  ["Finding synonyms and examples…", "Building the full vocabulary picture"],
  ["Painting the illustration…", "Creating a bespoke image — almost there"],
];
const WR_STEPS = ["Type a word", "Bloom works its magic", "Ready to learn"];
const WR_POINTS = ["A child-friendly meaning, synonyms and example sentences", "An illustration in the same style as every Bloom card", "Added straight to each child's priority list"];

// mode "auto": types, glows, presses Generate itself, loops. mode "tap": waits for the parent to press Generate.
function WordRequestSection({ mode = "auto" }) {
  const [stage, setStage] = useState(0);   // 0 input · 1–4 working · 5 done
  const [typed, setTyped] = useState(0);
  const [pulse, setPulse] = useState(false);
  const timers = useRef([]);
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));

  const start = useCallback(() => {
    clear(); setStage(0); setTyped(0);
    for (let i = 1; i <= WR_WORD.length; i++) later(() => setTyped(i), 500 + i * 120);
    if (mode === "auto") later(() => generate(true), 500 + WR_WORD.length * 120 + 2200);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = (force) => {
    if (!force && stage !== 0) return;
    clear(); setTyped(WR_WORD.length); setStage(1);
    [2, 3, 4, 5].forEach((st, i) => later(() => setStage(st), (i + 1) * 1700));
    if (mode === "auto") later(start, 4 * 1700 + 8000);
  };
  const showDone = () => { clear(); setTyped(WR_WORD.length); setStage(5); if (mode === "auto") later(start, 8000); };

  useEffect(() => { const t = setInterval(() => setPulse(p => !p), 850); return () => clearInterval(t); }, []);
  useEffect(() => { start(); return clear; }, [start]);

  const ready = stage === 0 && typed >= WR_WORD.length;
  const glow = ready && pulse;
  const phase = stage === 0 ? 0 : stage === 5 ? 2 : 1;
  const stepActions = [start, () => generate(true), showDone];
  const chip = (bg, fg, t) => <span key={t} style={{ padding: "3px 10px", borderRadius: 999, background: bg, color: fg }}>{t}</span>;
  const cap = { font: "700 12px 'Quicksand',sans-serif", letterSpacing: ".1em", color: "#6E7289" };

  return (
    <section style={{ background: "#FFFFFF", borderTop: "1px solid #F4EAE0", borderBottom: "1px solid #F4EAE0" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(56px,8cqw,96px) clamp(20px,5cqw,48px)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,380px),1fr))", gap: "clamp(32px,5cqw,64px)", alignItems: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 480, minWidth: 0 }}>
          <span style={{ ...S.label, color: "#C2410C", display: "flex", alignItems: "center", gap: 8 }}><Sparkle size={16} weight="fill" />REQUEST A WORD</span>
          <h2 style={S.h2}>Missing a word? Bloom will make it.</h2>
          <p style={{ margin: 0, font: "500 17px/1.6 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>Type any English word your child has come across. Press Generate and Bloom writes the meaning, finds synonyms and examples, and paints a picture for it.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            {WR_POINTS.map(p => <CheckItem key={p} text={p} />)}
          </div>
          <p style={{ margin: "6px 0 0", font: "600 13.5px/1.5 'Quicksand',sans-serif", color: "#6E7289" }}>Up to 20 word requests a month · Reviewed by the Bloom team within 24 hours</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8 }}>
            {WR_STEPS.map((label, i) => {
              const on = i === phase, past = i < phase;
              return (
                <button key={label} onClick={stepActions[i]} style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, padding: "10px 12px", borderRadius: 16, border: 0, background: on ? "#FFF1E8" : "#F7F4F1", color: on ? "#B13C0A" : "#6E7289", font: "800 14px/1.25 'Nunito',sans-serif", textAlign: "left", cursor: "pointer", transition: "background .3s,color .3s" }}>
                  <span style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: on ? "#FF7A45" : past ? "#FBC9AE" : "#E8E2DB", color: on ? "#FFFFFF" : past ? "#B13C0A" : "#6E7289", display: "grid", placeItems: "center", font: "900 12.5px 'Nunito',sans-serif" }}>{i + 1}</span>
                  <span style={{ minWidth: 0 }}>{label}</span>
                </button>
              );
            })}
          </div>

          <div style={{ height: 540, borderRadius: 30, background: "#FFFFFF", border: "1px solid #ECE6DF", boxShadow: "0 18px 44px rgba(38,42,68,.10)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 24px", borderBottom: "1px solid #F4ECE4" }}>
              <span style={{ flexShrink: 0, width: 38, height: 38, borderRadius: "50%", background: "#F4F1EE", color: "#545872", display: "grid", placeItems: "center" }}><ArrowLeft size={16} weight="bold" /></span>
              <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                <span style={{ font: "900 19px 'Nunito',sans-serif" }}>Request a word</span>
                <span style={{ font: "500 13.5px 'Quicksand',sans-serif", color: "#6E7289" }}>Suggest a word for the Bloom library</span>
              </div>
            </div>

            {stage === 0 && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 16, padding: "28px clamp(20px,4cqw,32px)" }}>
                <p style={{ margin: 0, font: "500 16px/1.55 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>Enter any English word — we'll generate the meaning, examples and an illustration automatically.</p>
                <span style={{ font: "700 15px 'Quicksand',sans-serif" }}>Word</span>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                  <div style={{ flex: "999 1 220px", minWidth: 0, height: 60, boxSizing: "border-box", display: "flex", alignItems: "center", padding: "0 20px", borderRadius: 999, border: "2px solid #FF7A45", boxShadow: "0 0 0 5px #FFE9DC", font: "600 20px 'Quicksand',sans-serif" }}>
                    {WR_WORD.slice(0, typed)}<span style={{ width: 2, height: 24, marginLeft: 2, background: "#262A44", opacity: pulse ? 0 : 1 }} />
                  </div>
                  <button onClick={() => generate()} style={{ flex: "1 0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, height: 60, padding: "0 26px", border: 0, borderRadius: 999, background: "#FF7A45", color: "#FFFFFF", font: "800 18px 'Nunito',sans-serif", cursor: "pointer", transform: `scale(${glow ? 1.04 : 1})`, boxShadow: glow ? "0 0 0 8px rgba(255,122,69,.22), 0 12px 28px rgba(247,107,50,.40)" : "0 0 0 0 rgba(255,122,69,0), 0 10px 24px rgba(247,107,50,.30)", transition: "box-shadow .8s ease, transform .8s ease" }}>
                    <Sparkle size={20} weight="fill" />Generate
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, alignSelf: "flex-start", padding: "6px 14px", borderRadius: 999, background: "#F4F1EE", border: "1px solid #ECE6DF", font: "500 13.5px 'Quicksand',sans-serif", color: "#6E7289" }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#6E7289" }} /><b style={{ fontWeight: 700, color: "#3E4260" }}>3 / 20 used</b> this month
                </div>
              </div>
            )}

            {stage >= 1 && stage <= 4 && (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: "28px 24px", textAlign: "center" }}>
                <div style={{ marginBottom: 18, transform: `translateY(${pulse ? -6 : 0}px)`, transition: "transform .9s ease-in-out" }}><Mascot size={96} animated={false} /></div>
                <span style={{ font: "900 24px 'Nunito',sans-serif" }}>Working on <span style={{ color: "#C2410C" }}>"{WR_WORD}"</span></span>
                <span style={{ font: "700 18px 'Quicksand',sans-serif", color: "#C2410C" }}>{WR_WORK[stage - 1][0]}</span>
                <span style={{ font: "500 15.5px 'Quicksand',sans-serif", color: "#545872" }}>{WR_WORK[stage - 1][1]}</span>
                <div style={{ display: "flex", gap: 10, margin: "14px 0 8px" }}>
                  {[1, 2, 3, 4].map(i => <span key={i} style={{ height: 12, borderRadius: 99, width: i <= stage ? 40 : 12, background: i === stage ? "#C2410C" : i < stage ? "#FF7A45" : "#E8E4DD", transition: "width .35s,background .35s" }} />)}
                </div>
                <span style={{ font: "500 14.5px 'Quicksand',sans-serif", color: "#545872" }}>This takes about 30–45 seconds</span>
              </div>
            )}

            {stage === 5 && (
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,230px),1fr))", gap: 20, padding: "20px 24px 24px", alignItems: "start" }}>
                <img src="/assets/word-attire.webp" alt="Attire word card: children in their talent-show outfits" style={{ width: "100%", aspectRatio: "1/1", display: "block", borderRadius: 18, border: "1px solid #ECE6DF" }} />
                <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ font: "900 22px 'Nunito',sans-serif" }}>{WR_WORD}</span>
                    <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 999, background: "#D9F3E6", color: "#16704B", font: "800 12px 'Nunito',sans-serif" }}><Check size={12} weight="bold" />Ready</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, font: "600 13px 'Quicksand',sans-serif" }}>
                    {chip("#F1EEEA", "#545872", "noun")}{chip("#FFEFC9", "#8A5300", "medium")}{chip("#F1EEEA", "#545872", "Y5, Y6")}{chip("#DCEEF6", "#1D6485", "11+")}
                  </div>
                  <p style={{ margin: 0, font: "500 15px/1.5 'Quicksand',sans-serif", color: "#3E4260", textWrap: "pretty" }}>Attire means the clothes that someone wears, especially for a special occasion.</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <span style={cap}>SYNONYMS</span>
                    <span style={{ font: "500 14.5px 'Quicksand',sans-serif", color: "#3E4260" }}>clothing, outfit, garments</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={cap}>EXAMPLE</span>
                    <span style={{ padding: "10px 14px", borderRadius: 14, background: "#F7F4F1", font: "italic 500 14.5px/1.45 'Quicksand',sans-serif", color: "#545872" }}>"For the school play, everyone wore their best attire."</span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4, paddingTop: 14, borderTop: "1px solid #F0E8E0" }}>
                    <span style={{ flex: "1 0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, height: 42, padding: "0 18px", borderRadius: 999, background: "#1FC98A", color: "#FFFFFF", font: "800 15px 'Nunito',sans-serif" }}><CheckCircle size={17} weight="bold" />Submit word</span>
                    <button onClick={start} style={{ flex: "1 0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, height: 42, padding: "0 16px", borderRadius: 999, border: "1.5px solid #E3DCD4", background: "#FFFFFF", color: "#262A44", font: "800 15px 'Nunito',sans-serif", cursor: "pointer" }}><ArrowsClockwise size={16} weight="bold" />Try another word</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function VocabLanding() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Auth redirect (unchanged)
  useEffect(() => {
    if (!loading && user)
      navigate(user.role === "admin" ? "/admin" : "/vocab/children", { replace: true });
  }, [user, loading, navigate]);

  // Google OAuth (unchanged)
  const signIn = useCallback(() => {
    const redirectUri = `${window.location.origin}/auth/callback`;
    const params = new URLSearchParams({
      client_id:     process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri:  redirectUri,
      response_type: "code",
      scope:         "openid email profile",
      prompt:        "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }, []);

  // Container width → wide/narrow
  const rootRef    = useRef(null);
  const ctaRef     = useRef(null);
  const trackRef   = useRef(null);
  const pricingRef = useRef(null);
  const codeInputRef = useRef(null);

  const [cw, setCw]           = useState(1200);
  const [ctaVisible, setCtaV] = useState(true);
  const [slideIdx, setSlide]  = useState(0);
  const [code, setCode]       = useState("");
  const [codeOpen, setCodeOpen] = useState(false);
  const [toast, setToast]     = useState("");
  const pausedUntil           = useRef(0);
  const toastTimer            = useRef(null);
  const autoTimer             = useRef(null);

  const wide   = cw >= 720;
  const codeOk = code.trim().length >= 4;

  // ── Observers + auto-slide ──────────────────────────────────────────────────
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setCw(e.contentRect.width));
    if (rootRef.current) ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setCtaV(e.isIntersecting));
    if (ctaRef.current) io.observe(ctaRef.current);
    return () => io.disconnect();
  }, []);

  const goSlide = useCallback((i) => {
    const t = trackRef.current;
    const els = t ? Array.from(t.querySelectorAll("[data-slide]")) : [];
    const n = Math.max(0, Math.min(SLIDES.length - 1, i));
    if (t && els.length) t.scrollTo({ left: els[n].offsetLeft - els[0].offsetLeft, behavior: "smooth" });
    setSlide(n);
  }, []);

  useEffect(() => {
    autoTimer.current = setInterval(() => {
      if (Date.now() < pausedUntil.current || document.hidden) return;
      if (!wide) { setSlide(i => (i + 1) % SLIDES.length); return; }
      goSlide((slideIdx + 1) % SLIDES.length);
    }, 4000);
    return () => clearInterval(autoTimer.current);
  }, [wide, slideIdx, goSlide]);

  const pauseAuto = () => { pausedUntil.current = Date.now() + 8000; };

  const onTrackScroll = () => {
    const t = trackRef.current;
    const els = t ? Array.from(t.querySelectorAll("[data-slide]")) : [];
    if (!t || !els.length) return;
    const base = els[0].offsetLeft;
    let best = 0, bd = 1e9;
    els.forEach((el, i) => { const d = Math.abs(el.offsetLeft - base - t.scrollLeft); if (d < bd) { bd = d; best = i; } });
    if (t.scrollLeft + t.clientWidth >= t.scrollWidth - 4) best = els.length - 1;
    if (best !== slideIdx) setSlide(best);
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const showToast = (msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(""), 2400);
  };

  const scrollTo = (el, offset = 24) => {
    if (!el) return;
    window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - offset, behavior: "smooth" });
  };

  const startTrial = () => {
    showToast("Opening Google sign-in…");
    signIn();
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Quicksand:wght@500;600;700&display=swap" rel="stylesheet" />

      <div ref={rootRef} style={S.root}>

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, maxWidth: 1240, margin: "0 auto", padding: "16px clamp(16px,5cqw,48px)" }}>
          <a href="#" onClick={e => e.preventDefault()} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 16px 6px 8px", background: "#FFFFFF", borderRadius: 999, boxShadow: "0 4px 18px rgba(194,65,12,.08)", color: "#262A44", textDecoration: "none" }}>
            <Mascot size={30} animated={false} />
            <span style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 900, fontSize: 17, letterSpacing: "-.01em" }}>Bloom Vocabulary</span>
          </a>
          <nav style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {wide && (
              <button onClick={() => scrollTo(pricingRef.current)} style={{ background: "none", border: 0, padding: "10px 14px", font: "700 15px 'Quicksand',sans-serif", color: "#545872", cursor: "pointer" }}>
                Pricing
              </button>
            )}
            <button onClick={signIn} style={{ height: 44, padding: "0 18px", borderRadius: 999, border: "1.5px solid #F2CDB6", background: "#FFFFFF", font: "700 15px 'Quicksand',sans-serif", color: "#C2410C", cursor: "pointer" }}>
              Sign in
            </button>
          </nav>
        </header>

        {/* ── Hero ───────────────────────────────────────────────────────────── */}
        <section style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(12px,4cqw,48px) clamp(20px,5cqw,48px) clamp(48px,7cqw,88px)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,460px),1fr))", gap: "clamp(40px,5cqw,64px)", alignItems: "center" }}>

          {/* Left: text + CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 22, minWidth: 0 }}>
            <h1 style={S.h1}>Grow a vocabulary that <span style={{ color: "#C2410C" }}>blooms</span></h1>
            <p style={S.body}>Bloom helps children in Years 3–6 learn, practise and master the words they need — including Grammar School and 11+ preparation.</p>

            {/* Journey pills */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, font: "700 14px 'Quicksand',sans-serif" }}>
              {[
                { label: "Learn",    bg: "#FFE4D4", fg: "#B13C0A" },
                { label: "Play",     bg: "#FFEFC9", fg: "#8A5300" },
                { label: "Practise", bg: "#D9F3E6", fg: "#16704B" },
                { label: "Master",   bg: "#DCEEF6", fg: "#1D6485" },
              ].map(({ label, bg, fg }, i, arr) => (
                <span key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ padding: "6px 12px", borderRadius: 999, background: bg, color: fg }}>{label}</span>
                  {i < arr.length - 1 && wide && <span style={{ color: "#B3A99F" }}>→</span>}
                </span>
              ))}
            </div>

            {/* CTA block */}
            <div ref={ctaRef} style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", maxWidth: 440, marginTop: 6 }}>

              {/* Optional promo-code field */}
              {codeOpen && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <label htmlFor="bloom-code" style={{ font: "700 14px 'Quicksand',sans-serif", color: "#262A44" }}>School or referral code</label>
                  <input
                    id="bloom-code"
                    ref={codeInputRef}
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase().slice(0, 16))}
                    placeholder="e.g. BLOOM-7DAY"
                    autoComplete="off"
                    spellCheck={false}
                    style={{ height: 54, padding: "0 18px", borderRadius: 16, border: `1.5px solid ${codeOk ? "#16704B" : "#EADBCB"}`, background: "#FFFFFF", font: "700 18px 'Quicksand',sans-serif", letterSpacing: ".08em", textTransform: "uppercase", color: "#262A44", outline: "none", width: "100%" }}
                  />
                  {codeOk && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, font: "700 14px 'Quicksand',sans-serif", color: "#16704B" }}>
                      <span style={{ width: 18, height: 18, borderRadius: "50%", background: "#16704B", color: "#FFFFFF", display: "grid", placeItems: "center", fontSize: 11 }}>✓</span>
                      Code looks good — 7 days free unlocked
                    </div>
                  )}
                </div>
              )}

              <GButton onClick={startTrial}>Start free for 7 days</GButton>


              <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "center", textAlign: "center" }}>
                <p style={{ margin: 0, font: "600 14px/1.45 'Quicksand',sans-serif", color: "#3E4260", textWrap: "pretty" }}>No card required · Parents sign in — children just tap their profile</p>
                <p style={{ margin: 0, font: "500 13px/1.45 'Quicksand',sans-serif", color: "#6E7289" }}>After 7 days: Monthly £6.49 or Annual 60.00 — cancel anytime</p>
              </div>
            </div>
          </div>

          {/* Right: stacked word + quiz images */}
          <div style={{ display: "grid", minWidth: 0, paddingBottom: 28 }}>
            <div style={{ gridArea: "1/1", alignSelf: "center", justifySelf: "center", width: "92%", maxWidth: 520, aspectRatio: 1, borderRadius: "50%", background: "#DDF5EA" }} />
            <img src="/assets/word-metaphor.webp" alt="Metaphor word card with illustrated picture" style={{ gridArea: "1/1", justifySelf: "end", alignSelf: "start", width: "78%", maxWidth: 380, aspectRatio: "5/8", display: "block", borderRadius: 26, boxShadow: "0 24px 60px rgba(38,42,68,.14)" }} />
            <img src="/assets/quiz-vile.webp" alt="Mixed quiz question" style={{ gridArea: "1/1", justifySelf: "start", alignSelf: "end", width: "66%", maxWidth: 340, aspectRatio: "5/3", display: "block", borderRadius: 22, boxShadow: "0 18px 44px rgba(38,42,68,.18)", transform: "translateY(28px) rotate(-3deg)" }} />
          </div>
        </section>

        {/* ── Why Bloom ──────────────────────────────────────────────────────── */}
        <section style={{ maxWidth: 1240, margin: "0 auto", padding: "clamp(24px,4cqw,40px) clamp(20px,5cqw,48px) clamp(56px,8cqw,96px)", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,440px),1fr))", gap: "clamp(32px,5cqw,64px)", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 520 }}>
            <span style={{ ...S.label, color: "#16704B" }}>WHY BLOOM?</span>
            <h2 style={S.h2}>Every word gets its own picture</h2>
            <p style={{ margin: 0, font: "500 17px/1.6 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>We create a child-friendly illustration for every word, so the meaning is easy to understand and easier to remember than a definition in a book or word list.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
              <CheckItem text="A picture that shows the meaning, drawn for children" />
              <CheckItem text="Simple meaning, example sentences and audio" />
              <CheckItem text="Synonyms and antonyms to build word families" />
            </div>
          </div>

          {/* Word list vs Bloom comparison */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,200px),1fr))", gap: 16, alignItems: "center", minWidth: 0 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span style={{ font: "700 12.5px 'Quicksand',sans-serif", letterSpacing: ".12em", color: "#6E7289" }}>A TYPICAL WORD LIST</span>
              <div style={{ padding: 20, borderRadius: 18, background: "#F4F1EE", border: "1px solid #E6E0DA", display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ font: "800 20px 'Nunito',sans-serif", color: "#545872" }}>peep <span style={{ font: "500 italic 15px 'Quicksand',sans-serif", color: "#6E7289" }}>verb</span></span>
                <span style={{ font: "500 15px/1.5 'Quicksand',sans-serif", color: "#545872" }}>To look quickly and furtively, especially through a narrow aperture.</span>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <span style={{ font: "700 12.5px 'Quicksand',sans-serif", letterSpacing: ".12em", color: "#16704B" }}>BLOOM</span>
              <img src="/assets/word-peep.webp" alt="Bloom's illustrated Peep card" style={{ width: "100%", maxWidth: 340, aspectRatio: "5/8", display: "block", borderRadius: 22, boxShadow: "0 14px 36px rgba(38,42,68,.12)" }} />
            </div>
          </div>
        </section>

        {/* ── Request a word (core feature) ──────────────────────────────────── */}
        <WordRequestSection mode="auto" />

        {/* ── Create a learning plan ─────────────────────────────────────────── */}
        <LearningPlanSection />

        {/* ── See Bloom in Action ────────────────────────────────────────────── */}
        <section style={{ padding: "clamp(56px,8cqw,96px) 0", background: "#FFFFFF", borderTop: "1px solid #F4EAE0", borderBottom: "1px solid #F4EAE0" }}>
          <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 clamp(20px,5cqw,48px)", display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 20 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 640 }}>
              <span style={{ ...S.label, color: "#16704B" }}>SEE BLOOM IN ACTION</span>
              <h2 style={S.h2}>Real words. Real practice. Real progress.</h2>
            </div>
            {wide && (
              <div style={{ display: "flex", gap: 10 }}>
                {["←", "→"].map((arrow, i) => (
                  <button key={arrow} onClick={() => { pauseAuto(); goSlide(slideIdx + (i === 0 ? -1 : 1)); }}
                    style={{ width: 48, height: 48, borderRadius: "50%", border: "1.5px solid #EADBCB", background: "#FFFFFF", font: "700 20px 'Nunito',sans-serif", color: "#262A44", cursor: "pointer" }}>
                    {arrow}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile: tab + crossfade */}
          {!wide && (
            <div onPointerDown={pauseAuto} style={{ padding: "24px 20px 0", display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", scrollbarWidth: "none", margin: "0 -20px", padding: "0 20px" }}>
                {SLIDES.map((s, i) => (
                  <button key={s.word} onClick={() => { pauseAuto(); setSlide(i); }}
                    style={{ flexShrink: 0, height: 42, padding: "0 16px", borderRadius: 999, border: `1.5px solid ${i === slideIdx ? "#262A44" : "#EADBCB"}`, background: i === slideIdx ? "#262A44" : "#FFFFFF", color: i === slideIdx ? "#FFFFFF" : "#3E4260", font: "700 15px 'Quicksand',sans-serif", cursor: "pointer", transition: "background .25s,color .25s" }}>
                    {s.word}
                  </button>
                ))}
              </div>
              <div style={{ position: "relative", height: 440, overflow: "hidden", borderRadius: 24, background: "#FFFFFF", boxShadow: "0 14px 36px rgba(38,42,68,.10)" }}>
                {SLIDES.map((s, i) => (
                  <img key={s.word} src={s.src} alt={s.alt} style={{ position: "absolute", top: 0, left: 0, width: "100%", display: "block", opacity: i === slideIdx ? 1 : 0, transition: "opacity .45s ease" }} />
                ))}
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 90, background: "linear-gradient(rgba(255,255,255,0),#FFFFFF)" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <p style={{ margin: 0, flex: 1, font: "600 15px/1.45 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>{SLIDES[slideIdx].caption}</p>
                <div style={{ flexShrink: 0, width: 56, height: 4, borderRadius: 99, background: "#EFE4DA", overflow: "hidden" }}>
                  <div style={{ width: `${((slideIdx + 1) / SLIDES.length) * 100}%`, height: "100%", background: "#F76B32", transition: "width .3s" }} />
                </div>
              </div>
            </div>
          )}

          {/* Desktop: scroll track */}
          {wide && (
            <>
              <div ref={trackRef} onScroll={onTrackScroll} onPointerDown={pauseAuto} onMouseEnter={pauseAuto}
                style={{ display: "flex", gap: 20, overflowX: "auto", scrollSnapType: "x mandatory", scrollPadding: "0 clamp(20px,5cqw,48px)", padding: "32px clamp(20px,5cqw,48px) 12px", scrollbarWidth: "none", alignItems: "flex-start", maxWidth: 1240, margin: "0 auto" }}>
                {SLIDES.map((s, i) => (
                  <figure key={s.word} data-slide={1} style={{ flexShrink: 0, flexBasis: "min(80%,340px)", margin: 0, scrollSnapAlign: "start", display: "flex", flexDirection: "column", gap: 14 }}>
                    <img src={s.src} alt={s.alt} style={{ width: "100%", aspectRatio: "5/8", display: "block", borderRadius: 24, boxShadow: "0 14px 36px rgba(38,42,68,.10)" }} />
                    <figcaption style={{ font: "600 15px/1.45 'Quicksand',sans-serif", color: "#545872", padding: "0 6px", textWrap: "pretty" }}>{s.caption}</figcaption>
                  </figure>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "center", gap: 6, paddingTop: 12 }}>
                {SLIDES.map((_, i) => (
                  <button key={i} onClick={() => { pauseAuto(); goSlide(i); }} aria-label={`Show card ${i + 1}`}
                    style={{ width: i === slideIdx ? 24 : 8, height: 8, borderRadius: 99, border: 0, padding: 0, background: i === slideIdx ? "#F76B32" : "#E3D6CA", cursor: "pointer", transition: "width .25s,background .25s" }} />
                ))}
              </div>
            </>
          )}
        </section>

        {/* ── Play & Practise ────────────────────────────────────────────────── */}
        <section style={S.section({ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))", gap: "clamp(32px,5cqw,64px)", alignItems: "center" })}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 520 }}>
            <span style={{ ...S.label, color: "#8A5300" }}>PLAY &amp; PRACTISE</span>
            <h2 style={S.h2sm}>Quick quizzes that check real understanding</h2>
            <p style={{ margin: 0, font: "500 17px/1.6 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>Mixed quizzes ask for meanings, synonyms and opposites. Every word can be heard aloud, and streaks keep children coming back.</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 0 }}>
            <img src="/assets/quiz-futile.webp" alt="Quiz: opposite of Futile" style={{ width: "100%", maxWidth: 520, aspectRatio: "5/3", display: "block", borderRadius: 22, boxShadow: "0 14px 36px rgba(38,42,68,.10)" }} />
            <img src="/assets/quiz-vile.webp"   alt="Quiz: opposite of Vile"   style={{ width: "100%", maxWidth: 520, aspectRatio: "5/3", display: "block", borderRadius: 22, boxShadow: "0 14px 36px rgba(38,42,68,.10)", alignSelf: "flex-end" }} />
          </div>
        </section>

        {/* ── Words for every stage ──────────────────────────────────────────── */}
        <section style={S.section({ display: "flex", flexDirection: "column", gap: 28, paddingBottom: 0 })}>
          <h2 style={S.h2sm}>Words for every stage</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,240px),1fr))", gap: 14 }}>
            {[
              { title: "Years 3–4 vocabulary",    bg: "#FFE9DC", fg: "#B13C0A", body: "Everyday words that build confident readers and writers." },
              { title: "Years 5–6 vocabulary",    bg: "#D9F3E6", fg: "#16704B", body: "Richer words for stories, comprehension and writing." },
              { title: "Grammar School & 11+",    bg: "#DCEEF6", fg: "#1D6485", body: "Grammar School and 11+ vocabulary included." },
            ].map(({ title, bg, fg, body }) => (
              <div key={title} style={{ padding: "22px 22px 24px", borderRadius: 22, background: bg, display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ font: "900 20px 'Nunito',sans-serif", color: fg }}>{title}</span>
                <span style={{ font: "500 15px/1.5 'Quicksand',sans-serif", color: "#4A4E68" }}>{body}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Pricing ────────────────────────────────────────────────────────── */}
        <section ref={pricingRef} style={S.section({ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center", paddingBottom: 0 })}>
          <span style={{ ...S.label, color: "#C2410C" }}>PRICING</span>
          <h2 style={S.h2}>One plan covers the whole family</h2>
          <p style={{ margin: 0, font: "500 17px/1.55 'Quicksand',sans-serif", color: "#545872", maxWidth: "32em", textWrap: "pretty" }}>Every family starts with 7 days free. No card needed until you choose a plan.</p>

          <div style={{ width: "100%", maxWidth: 820, marginTop: 16, padding: "14px 20px", borderRadius: 16, background: "#FFF1E8", border: "1.5px solid #FBBF9A", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "6px 16px", textAlign: "center" }}>
            <span style={{ font: "800 15px 'Nunito',sans-serif", color: "#B13C0A" }}>🎉 Launch offer — 40% off the Annual plan</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8, font: "600 14px 'Quicksand',sans-serif", color: "#545872" }}>
              Use code <span style={{ padding: "3px 10px", borderRadius: 6, background: "#262A44", color: "#FFFFFF", font: "800 13.5px 'Nunito',sans-serif", letterSpacing: ".06em" }}>BLOOM40</span> · Ends 25 Oct
            </span>
          </div>

          <div style={{ width: "100%", maxWidth: 820, marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,300px),1fr))", gap: 16, textAlign: "left" }}>
            {/* Annual */}
            <div style={{ position: "relative", padding: "28px 24px 24px", borderRadius: 26, background: "#FFFFFF", border: "2px solid #F76B32", boxShadow: "0 18px 44px rgba(247,107,50,.14)", display: "flex", flexDirection: "column", gap: 14 }}>
              <span style={{ position: "absolute", top: -13, left: 22, padding: "5px 12px", borderRadius: 999, background: "#F76B32", color: "#FFFFFF", font: "800 11.5px 'Nunito',sans-serif", letterSpacing: ".1em" }}>BEST VALUE</span>
              <span style={{ font: "800 14px 'Nunito',sans-serif", letterSpacing: ".08em", color: "#B13C0A" }}>ANNUAL</span>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ font: "900 42px 'Nunito',sans-serif", letterSpacing: "-.02em" }}>60.00</span>
                <span style={{ font: "600 15px 'Quicksand',sans-serif", color: "#6E7289" }}>/year</span>
              </div>
              <span style={{ font: "600 14.5px 'Quicksand',sans-serif", color: "#16704B" }}>Works out at £5.00 a month</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, padding: "6px 0 4px" }}>
                {ANNUAL_FEATURES.map(f => <div key={f} style={{ display: "flex", gap: 10, font: "500 15px/1.4 'Quicksand',sans-serif", color: "#3E4260" }}><span style={{ color: "#16704B", fontWeight: 800 }}>✓</span>{f}</div>)}
              </div>
              <GButton onClick={startTrial} style={{ marginTop: "auto", height: 52, fontSize: 16 }}>Start 7 days free</GButton>
            </div>

            {/* Monthly */}
            <div style={{ padding: "28px 24px 24px", borderRadius: 26, background: "#FFFFFF", border: "1.5px solid #EFE2D6", display: "flex", flexDirection: "column", gap: 14 }}>
              <span style={{ font: "800 14px 'Nunito',sans-serif", letterSpacing: ".08em", color: "#545872" }}>MONTHLY</span>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ font: "900 42px 'Nunito',sans-serif", letterSpacing: "-.02em" }}>£6.49</span>
                <span style={{ font: "600 15px 'Quicksand',sans-serif", color: "#6E7289" }}>/month</span>
              </div>
              <span style={{ font: "600 14.5px 'Quicksand',sans-serif", color: "#6E7289" }}>Recurring, no long-term commitment</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 9, padding: "6px 0 4px" }}>
                {MONTHLY_FEATURES.map(f => <div key={f} style={{ display: "flex", gap: 10, font: "500 15px/1.4 'Quicksand',sans-serif", color: "#3E4260" }}><span style={{ color: "#16704B", fontWeight: 800 }}>✓</span>{f}</div>)}
              </div>
              <button onClick={startTrial} style={{ marginTop: "auto", height: 52, borderRadius: 999, border: "1.5px solid #F2B79A", background: "#FFFFFF", color: "#C2410C", font: "800 16.5px 'Nunito',sans-serif", cursor: "pointer" }}>
                Start 7 days free
              </button>
            </div>
          </div>

          {/* 7-day pass
          <div style={{ width: "100%", maxWidth: 820, marginTop: 4, padding: "18px 22px", borderRadius: 20, background: "#FFFFFF", border: "1.5px dashed #E7D5C5", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px 20px", textAlign: "left" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ font: "800 16px 'Nunito',sans-serif" }}>Not ready to subscribe? <span style={{ color: "#C2410C" }}>7-day pass £2.99</span></span>
              <span style={{ font: "500 14px/1.45 'Quicksand',sans-serif", color: "#6E7289" }}>One-time payment, full access for 7 days, no auto-renew.</span>
            </div>
            <button onClick={startTrial} style={{ height: 44, padding: "0 18px", borderRadius: 999, border: "1.5px solid #E7D5C5", background: "#FFF7F0", font: "700 15px 'Quicksand',sans-serif", color: "#262A44", cursor: "pointer" }}>
              Buy a 7-day pass
            </button>
          </div> */}

          <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4, alignItems: "center", textAlign: "center" }}>
            <p style={{ margin: 0, font: "500 13px 'Quicksand',sans-serif", color: "#6E7289" }}>Secure payment by Stripe · Cancel anytime from your Billing page</p>
            <p style={{ margin: 0, font: "500 13px 'Quicksand',sans-serif", color: "#6E7289" }}>
              7-day free trial ·{" "}
              <a href="/vocab/refund-policy" style={{ color: "#C2410C", fontWeight: 700, textDecoration: "underline", textUnderlineOffset: 3 }}>
                Refund Policy
              </a>
            </p>
          </div>
        </section>

        {/* ── How the free week works ────────────────────────────────────────── */}
        <section style={S.section({ display: "flex", flexDirection: "column", gap: 28 })}>
          <h2 style={S.h2sm}>How the free week works</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,220px),1fr))", gap: 14 }}>
            {TRIAL_STEPS.map(({ when, bg, fg, title, body }) => (
              <div key={when} style={{ padding: 22, borderRadius: 22, background: "#FFFFFF", border: "1px solid #F0E4D8", display: "flex", flexDirection: "column", gap: 10 }}>
                <span style={{ alignSelf: "flex-start", padding: "5px 11px", borderRadius: 999, background: bg, color: fg, font: "800 13px 'Nunito',sans-serif" }}>{when}</span>
                <span style={{ font: "800 18px/1.25 'Nunito',sans-serif" }}>{title}</span>
                <span style={{ font: "500 15px/1.5 'Quicksand',sans-serif", color: "#545872", textWrap: "pretty" }}>{body}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Bottom CTA ─────────────────────────────────────────────────────── */}
        <section style={S.section({ paddingTop: "clamp(64px,9cqw,112px)" })}>
          <div style={{ padding: "clamp(32px,6cqw,56px) clamp(22px,5cqw,56px)", borderRadius: 32, background: "#FFE9DC", display: "flex", flexDirection: "column", alignItems: "center", gap: 18, textAlign: "center" }}>
            <Mascot size={72} animated={false} />
            <h2 style={S.h2}>Give your child a week of new words</h2>
            <GButton onClick={startTrial} style={{ maxWidth: 380, boxShadow: "0 10px 24px rgba(247,107,50,.3)" }}>Start free for 7 days</GButton>
            <span style={{ font: "600 14px 'Quicksand',sans-serif", color: "#6B3A1E" }}>No card required · Cancel anytime</span>
          </div>
        </section>

        {/* ── Footer ─────────────────────────────────────────────────────────── */}
        <footer style={{ borderTop: "1px solid #F0E4D8" }}>
          <div style={{ maxWidth: 1240, margin: "0 auto", padding: `24px clamp(20px,5cqw,48px) ${!wide ? 100 : 28}px`, display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "14px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Mascot size={24} animated={false} />
              <span style={{ font: "800 15px 'Nunito',sans-serif" }}>Bloom Vocabulary</span>
              <span style={{ font: "500 14px 'Quicksand',sans-serif", color: "#6E7289" }}>© 2026</span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px", font: "600 14px 'Quicksand',sans-serif" }}>
              {[
                { label: "Privacy",       href: "/vocab/privacy" },
                { label: "Terms",         href: "/vocab/terms" },
                { label: "Refund Policy", href: "/vocab/refund-policy" },
              ].map(({ label, href }) => (
                <a key={label} href={href} style={{ color: "#545872" }}>{label}</a>
              ))}
              <span style={{ color: "#6E7289" }}>Built by <a href="https://webuilthere.com" target="_blank" rel="noopener noreferrer" style={{ color: "#C2410C", fontWeight: 700 }}>We Built Here</a></span>
            </div>
          </div>
        </footer>

        {/* ── Sticky mobile CTA ──────────────────────────────────────────────── */}
        {!wide && (
          <div style={{ position: "sticky", bottom: 0, zIndex: 20, padding: "12px 16px calc(12px + env(safe-area-inset-bottom))", background: "rgba(255,247,240,.94)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)", borderTop: "1px solid #F0E4D8", transform: `translateY(${ctaVisible ? 110 : 0}%)`, transition: "transform .3s ease", marginTop: -80 }}>
            <GButton onClick={startTrial} style={{ height: 54, fontSize: 17.5 }}>Start free for 7 days</GButton>
          </div>
        )}

        {/* ── Toast ──────────────────────────────────────────────────────────── */}
        {toast && (
          <div style={{ position: "fixed", bottom: 88, left: 0, right: 0, zIndex: 30, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
            <div style={{ padding: "12px 18px", borderRadius: 14, background: "#262A44", color: "#FFFFFF", font: "600 14.5px 'Quicksand',sans-serif", boxShadow: "0 10px 30px rgba(0,0,0,.2)" }}>{toast}</div>
          </div>
        )}

      </div>
    </>
  );
}